import React, { useState } from "react";
import { Copy, CheckCircle } from "lucide-react";
import { ProcessedInsight } from "./types";
import { getDisplayName, parseInsightSummary } from "./utils";
import { LabelMapping } from "./types";
import { epochToFormattedDateTime } from "../../lib/utils";
import SummaryDisplay from "./SummaryDisplay";

interface InsightsPopUpProps {
  data: ProcessedInsight;
  labelMappings: LabelMapping[];
  onClose: () => void;
}

const InsightsPopUp: React.FC<InsightsPopUpProps> = ({
  data,
  labelMappings,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopySummary = () => {
    if (!data.summary) return;
    const parsed = parseInsightSummary(data.summary);
    const text = parsed?.formatted ?? data.summary;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const rows: { key: string; value: string }[] = [
    { key: "Artifact", value: getDisplayName(data.artifact, labelMappings) },
    { key: "Patient ID", value: data.patient_id },
    {
      key: "Start",
      value: epochToFormattedDateTime(data.startTime.toString()),
    },
    {
      key: "End",
      value: epochToFormattedDateTime(data.endTime.toString()),
    },
    { key: "Start Timestamp", value: String(data.startTime) },
    { key: "End Timestamp", value: String(data.endTime) },
    {
      key: "Duration",
      value: data.isInstant
        ? `${data.duration} ms (instant event, min bar width)`
        : `${data.time_taken_ms} ms`,
    },
    { key: "Errors", value: data.errors.length ? data.errors.join("; ") : "—" },
    {
      key: "Warnings",
      value: data.warnings.length ? data.warnings.join("; ") : "—",
    },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-75">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="title flex justify-between">
          <h2 className="text-xl font-bold mb-4">Insight Details</h2>
          <span
            onClick={onClose}
            className="font-bold text-lg text-red-600 cursor-pointer"
          >
            X
          </span>
        </div>

        <div className="max-w-2xl overflow-x-auto bg-gray-100 p-4 rounded">
          <table className="w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border px-2 py-1 text-left">Key</th>
                <th className="border px-2 py-1 text-left">Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-b">
                  <td className="border px-2 py-1">{row.key}</td>
                  <td className="border px-2 py-1 break-all">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.summary && (
          <div className="mt-4 p-4 bg-gray-100 rounded">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold">Summary:</h3>
              <button
                onClick={handleCopySummary}
                className="text-gray-600 hover:text-black"
                type="button"
              >
                {copied ? (
                  <CheckCircle className="text-green-500" />
                ) : (
                  <Copy />
                )}
              </button>
            </div>
            <SummaryDisplay summary={data.summary} />
          </div>
        )}

        <div className="flex justify-end space-x-4 mt-4">
          <button
            onClick={onClose}
            type="button"
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsightsPopUp;
